import {ReactNode, useState} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useGetLinksFAQQuery} from 'modules/seoApi'
import {getSeoLinkParams} from 'modules/seoApi/selectors'
import {useRouter} from 'next/router'

import {Accordion} from '@vio/atlas/Accordion'
import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {AboutFAQs} from './AboutFAQs'

const StyledText = styled(Text)`
  padding-bottom: ${cssTheme.layout.spacing.s400};
`

const FAQWrapper = ({
  isMobile,
  children
}: {
  isMobile: boolean
  children: ReactNode
}) => {
  if (isMobile)
    return (
      <Box
        borderRadius="lg"
        borderWidth="bw100"
        borderStyle="solid"
        borderColor="border.neutral.secondary"
      >
        {children}
      </Box>
    )

  return <div>{children}</div>
}

export const SrpFAQs = () => {
  const {query} = useRouter()
  const linkParams = useSelector(state =>
    getSeoLinkParams(state, query.taxonomy)
  )
  const [isOpen, setIsOpen] = useState(false)
  const {faqs} = useGetLinksFAQQuery(linkParams, {
    selectFromResult: result => ({
      ...result,
      faqs: result.data?.faqs || []
    }),
    skip: !linkParams.placeId
  })

  const {isMobile} = useDeviceLayout()

  if (!faqs.length) return null

  return (
    <>
      <Box
        marginTop={isMobile ? 's400' : 's300'}
        paddingLeft={isMobile ? undefined : 's400'}
        paddingRight={isMobile ? undefined : 's400'}
        marginBottom="s800"
      >
        <Divider />
      </Box>
      <Box
        data-id="srpFaqs"
        paddingTop={isMobile ? undefined : 's500'}
        paddingLeft={isMobile ? undefined : 's400'}
        paddingRight={isMobile ? undefined : 's400'}
      >
        <Box display="flex" flexDirection="column" gap="s500">
          <Text variant="titleXL">
            <FormattedMessageWrapper id="srpFAQs.title" defaultMessage="FAQs" />
          </Text>
          <FAQWrapper isMobile={isMobile}>
            {faqs.map((faq, index) => {
              return (
                <Accordion
                  key={index}
                  header={faq.question}
                  icon="DoubleChatBubble"
                  size="md"
                  sidePaddings={isMobile ? 's400' : undefined}
                  withDivider={!isMobile || faqs.length !== index + 1}
                >
                  <StyledText>{faq.answer}</StyledText>
                </Accordion>
              )
            })}
          </FAQWrapper>
        </Box>
        <Box
          marginBottom={isMobile ? 's600' : 's800'}
          marginTop="s400"
          display="flex"
          alignItems="flex-end"
          gap="s100"
          cursor="pointer"
          width="fit-content"
          role="button"
          onClick={() => setIsOpen(true)}
        >
          <Text variant="bodyXS">
            <FormattedMessageWrapper
              id="srpFAQs.disclaimerTitle"
              defaultMessage="About these FAQs"
            />
          </Text>

          <Icon size="sm" name="Information" colorPath="neutral.c600" />
        </Box>
      </Box>
      <AboutFAQs isOpen={isOpen} handleClose={() => setIsOpen(false)} />
    </>
  )
}
